import type { BlobPersistence } from "../../application/services/document-storage-service.js";

const DB_NAME = "inspect-ia";
const DB_VERSION = 1;
const STORE = "document-blobs";

/**
 * Arquivos do modo navegador no IndexedDB — espaço de centenas de MB, que
 * sobrevive a recarregar, sair e fechar o navegador (o localStorage tem ~5 MB
 * para TUDO e estourava com fotos e logos).
 */
export class IndexedDbBlobPersistence implements BlobPersistence {
  private db: Promise<IDBDatabase> | null = null;

  constructor(private readonly factory: IDBFactory = indexedDB) {}

  private open(): Promise<IDBDatabase> {
    if (!this.db) {
      this.db = new Promise<IDBDatabase>((resolve, reject) => {
        const request = this.factory.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
          if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new Error("IndexedDB bloqueado por outra aba"));
      });
      this.db.catch(() => { this.db = null; });
    }
    return this.db;
  }

  private async run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
    const db = await this.open();
    return new Promise<T | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const request = work(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(request ? request.result : undefined);
      tx.onerror = () => reject(tx.error ?? (request ? request.error : null));
      tx.onabort = () => reject(tx.error ?? new Error("Gravação cancelada pelo navegador"));
    });
  }

  async get(ref: string): Promise<string | null> {
    const value = await this.run<string>("readonly", store => store.get(ref) as IDBRequest<string>);
    return typeof value === "string" ? value : null;
  }

  async put(ref: string, content: string): Promise<void> {
    await this.run("readwrite", store => { store.put(content, ref); });
  }

  async putMany(entries: ReadonlyArray<readonly [string, string]>): Promise<void> {
    await this.run("readwrite", store => { for (const [ref, content] of entries) store.put(content, ref); });
  }

  async delete(ref: string): Promise<void> {
    await this.run("readwrite", store => { store.delete(ref); });
  }
}
